import {Component, computed, inject, Injector, input, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MatDialogModule} from "@angular/material/dialog"
import {MatInputModule} from "@angular/material/input"
import {MatSelectModule} from "@angular/material/select"
import {MatTooltipModule} from "@angular/material/tooltip"
import {RouterModule} from "@angular/router"
import {ContentTypeModel, MutationUpdateTemplateInput, SortOrder, TemplateOrderByCriteria, TemplateState} from "@generated"
import {
    CreateTemplateForGridGQL,
    CreateTemplateRevisionForGridGQL,
    GetTemplateGridItemsGQL,
    GetTemplateGridItemsQuery,
    GetTemplateGridItemsQueryVariables,
    TemplateGridItemFragment,
    UpdateTemplateGridGQL,
} from "@app/templates/templates-grid/templates-grid.generated"
import {CardErrorComponent, CardPlaceholderComponent} from "@common/components/cards"
import {EntityCardComponent} from "@common/components/entity/entity-card/entity-card.component"
import {ItemListComponent, ListInfoComponent} from "@common/components/item"
import {InfiniteListComponent} from "@common/components/lists"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {Labels} from "@labels"
import {PaymentStateLabelComponent} from "@platform/components/shared/payment-state-label/payment-state-label.component"
import {StateLabelComponent} from "@platform/components/shared/state-label/state-label.component"
import {CancellableRequest} from "@platform/models/data/cancellable-request"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-templates-grid",
    imports: [
        CardErrorComponent,
        CardPlaceholderComponent,
        EntityCardComponent,
        FormsModule,
        InfiniteListComponent,
        ListInfoComponent,
        MatButtonModule,
        MatDialogModule,
        MatInputModule,
        MatSelectModule,
        PaymentStateLabelComponent,
        RouterModule,
        StateLabelComponent,
        MatTooltipModule,
    ],
    templateUrl: "./templates-grid.component.html",
    styleUrl: "./templates-grid.component.scss",
})
export class TemplatesGridComponent extends ItemListComponent<TemplateGridItemFragment, MutationUpdateTemplateInput> implements OnInit {
    protected override _contentTypeModel = ContentTypeModel.Template
    readonly $stateLabels = signal(Array.from(Labels.TemplateState.values()))

    public readonly $viewMode = input<"scenes" | "products" | undefined>(undefined, {alias: "viewMode"})

    readonly templatesGql = inject(GetTemplateGridItemsGQL)
    readonly createTemplate = inject(CreateTemplateForGridGQL)
    readonly createTemplateRevision = inject(CreateTemplateRevisionForGridGQL)
    readonly updateTemplateGql = inject(UpdateTemplateGridGQL)
    readonly injector = inject(Injector)

    private fetchRequest = new CancellableRequest<GetTemplateGridItemsQuery, GetTemplateGridItemsQueryVariables>(
        this.templatesGql,
        this.injector,
        this.destroyRef,
    )

    public readonly $entityName = computed(() => {
        const viewMode = this.$viewMode()
        if (viewMode === "scenes") return "scene"
        else if (viewMode === "products") return "product"
        else return "template"
    })

    private readonly $filterFunction = computed(() => {
        const viewMode = this.$viewMode()
        if (viewMode === "scenes") return this.filters.sceneFilter
        else if (viewMode === "products") return this.filters.productFilter
        else return this.filters.templateFilter
    })

    // OVERLOADS
    protected override openNewItem: () => boolean = () => false
    protected override _createItem = undefined

    protected override _fetchList = ({skip, take}: {skip: number; take: number}) =>
        this.fetchRequest
            .fetch({
                take: take,
                skip: skip,
                filter: this.$filterFunction()(),
                orderBy: [
                    {key: TemplateOrderByCriteria.Name, direction: SortOrder.Asc},
                    {key: TemplateOrderByCriteria.Id, direction: SortOrder.Desc},
                ],
            })
            .then(({templates, templatesCount}) => ({items: templates, totalCount: templatesCount}))

    protected override _refreshItem = ({id, legacyId}: {id?: string; legacyId?: number}) =>
        fetchThrowingErrors(this.templatesGql)({
            take: 1,
            filter: {
                ...this.$filterFunction()(),
                id: id ? {equals: id} : undefined,
                legacyId: legacyId ? {equals: legacyId} : undefined,
            },
        }).then(({templates}) => templates?.[0] || undefined)

    protected override _updateItem = (data: MutationUpdateTemplateInput) =>
        mutateThrowingErrors(this.updateTemplateGql)({
            input: data,
        }).then(({updateTemplate: template}) => template)

    templateLink = (template: TemplateGridItemFragment): string[] | undefined => {
        if (this.$can().update.template(template)) {
            if (!template.latestRevision) {
                return ["./", template.id, "revisions", "add"]
            } else {
                return ["./", template.id, "revisions", template.latestRevision.id, "edit"]
            }
        }
        return undefined
    }

    multiThumbnailIds = (template: TemplateGridItemFragment) => {
        if (template?.galleryImage?.id) {
            return null
        }
        return template?.latestRevision?.renderedImages?.map(({id}) => id) ?? null
    }

    _copyTemplate = async (_template: TemplateGridItemFragment) => {}

    copyTemplate = async (template: TemplateGridItemFragment) => {
        const targetOrganization = this.organizations.$current()
        await this.notifications.withUserFeedback(
            async () => {
                const {createTemplate: newTemplate} = await mutateThrowingErrors(this.createTemplate)({
                    input: {
                        name: `Copy of ${template.name}`,
                        organizationId: targetOrganization?.id ?? template.organization.id,
                        state: TemplateState.Draft,
                        templateType: template.type,
                        comment: template.comment,
                        public: template.public,
                    },
                })

                if (template.latestRevision) {
                    const {createTemplateRevision: newTemplateRevision} = await mutateThrowingErrors(this.createTemplateRevision)({
                        input: {
                            templateId: newTemplate.id,
                            completed: false,
                            graph: template.latestRevision.graph,
                        },
                    })

                    await this.router.navigate([newTemplate.id, "revisions", newTemplateRevision.id, "edit"], {
                        relativeTo: this.route,
                        queryParamsHandling: "preserve",
                    })
                } else {
                    await this.router.navigate([newTemplate.id], {
                        relativeTo: this.route,
                        queryParamsHandling: "preserve",
                    })
                }
            },
            {
                success: "Template copied successfully",
                error: "Cannot copy template",
            },
        )
    }
}
